import type { ApolloCache, Reference, StoreObject } from '@apollo/client'
import type { ModifierDetails } from '@apollo/client/cache'

import { rememberPostScheduledTimeChange } from '~publish/helpers/posts/recentPostScheduledTimeChanges'
import type {
  DateTimePresence,
  PostSortInput,
} from '~publish/pages/AllChannels/PostList/usePaginatedPostList'

import type { QueryVariables } from './useCalendarAndPostsList'

type PostStatus = NonNullable<QueryVariables['status']>[number]
type TagComparator = NonNullable<QueryVariables['tags']>
type SortDirection = PostSortInput['direction']

type PostConnectionEdge = {
  __typename?: 'PostsEdge'
  node: Reference | StoreObject
}

type PostConnection = StoreObject & {
  edges?: PostConnectionEdge[] | null
}

type DueAtFilter = {
  start?: string | null
  end?: string | null
}

type PostsFilter = {
  channelIds?: string[] | string | null
  dueAt?: DueAtFilter | null
  dueAtPresence?: DateTimePresence | null
  status?: PostStatus[] | PostStatus | null
  tags?: TagComparator | null
}

type PostsFieldArgs = {
  input?: {
    filter?: PostsFilter | null
    sort?: PostSortInput[] | PostSortInput | null
  } | null
}

type PostScheduleCacheUpdate = {
  id: string
  dueAt: string | null
  status?: PostStatus
  channelId?: string | null
  tagIds?: string[]
  isCustomScheduled?: boolean
  isPinned?: boolean
}

type PostFilterSnapshot = {
  id: string
  dueAt: string | null
  status?: PostStatus
  channelId?: string | null
  tagIds?: string[]
}

export function updatePostScheduledTimeCache(
  cache: ApolloCache,
  post: PostScheduleCacheUpdate,
): void {
  rememberPostScheduledTimeChange(post.id, post.dueAt)

  const cacheId = cache.identify({ __typename: 'Post', id: post.id })
  if (cacheId) {
    cache.modify({
      id: cacheId,
      fields: {
        dueAt: (): string | null => post.dueAt,
        ...(typeof post.isCustomScheduled === 'boolean'
          ? {
              isCustomScheduled: (): boolean => post.isCustomScheduled === true,
            }
          : {}),
        ...(typeof post.isPinned === 'boolean'
          ? { isPinned: (): boolean => post.isPinned === true }
          : {}),
      },
    })
  }

  cache.modify({
    fields: {
      posts: (
        existingPosts: Reference | StoreObject,
        details: ModifierDetails,
      ) => {
        if (!isPostConnection(existingPosts)) return existingPosts

        const connection = existingPosts
        const edges = connection.edges
        if (!edges) return connection

        const existingEdge = edges.find(
          (edge) => details.readField('id', edge.node) === post.id,
        )
        const args = parsePostsFieldArgs(details.storeFieldName)
        if (!args) return connection

        const postReference = details.toReference({
          __typename: 'Post',
          id: post.id,
        })
        const postSnapshot = getPostFilterSnapshot(
          post,
          existingEdge?.node ?? postReference,
          details,
        )
        const isDueAtConstrained = isConnectionConstrainedByDueAt(args)
        const shouldInclude = doesPostMatchConnection(args, postSnapshot)

        if (!existingEdge && (!isDueAtConstrained || !shouldInclude)) {
          return connection
        }

        if (!postReference) return connection

        const nextEdges = edges.filter(
          (edge) => details.readField('id', edge.node) !== post.id,
        )

        if (shouldInclude) {
          nextEdges.push(createUpdatedEdge(existingEdge, postReference))
          sortEdgesByDueAt(nextEdges, details.readField, args.input?.sort)
        }

        return {
          ...connection,
          edges: nextEdges,
        }
      },
    },
  })
}

function createUpdatedEdge(
  edge: PostConnectionEdge | undefined,
  node: Reference,
): PostConnectionEdge {
  return edge ? { ...edge, node } : { __typename: 'PostsEdge', node }
}

function getPostFilterSnapshot(
  post: PostScheduleCacheUpdate,
  node: Reference | StoreObject | undefined,
  { readField }: ModifierDetails,
): PostFilterSnapshot {
  return {
    id: post.id,
    dueAt: post.dueAt,
    status: post.status ?? readField<PostStatus>('status', node),
    channelId:
      post.channelId ??
      readField<string>('channelId', node) ??
      readField<string>('id', readField('channel', node)),
    tagIds:
      post.tagIds ??
      readField<ReadonlyArray<Reference | StoreObject>>('tags', node)
        ?.map((tag) => readField<string>('id', tag))
        .filter(isString),
  }
}

function sortEdgesByDueAt(
  edges: PostConnectionEdge[],
  readField: ModifierDetails['readField'],
  sort: PostSortInput[] | PostSortInput | null | undefined,
): void {
  const direction = getDueAtSortDirection(sort)
  edges.sort((leftEdge, rightEdge) => {
    const leftDueAt = readField<string | null>('dueAt', leftEdge.node)
    const rightDueAt = readField<string | null>('dueAt', rightEdge.node)

    if (!leftDueAt && !rightDueAt) return 0
    if (!leftDueAt) return direction === 'asc' ? -1 : 1
    if (!rightDueAt) return direction === 'asc' ? 1 : -1

    const sortValue =
      new Date(leftDueAt).getTime() - new Date(rightDueAt).getTime()
    return direction === 'asc' ? sortValue : -sortValue
  })
}

function getDueAtSortDirection(
  sort: PostSortInput[] | PostSortInput | null | undefined,
): SortDirection {
  const dueAtSort = normalizeArray(sort).find((sortInput) => {
    return sortInput.field === 'dueAt'
  })

  return dueAtSort?.direction ?? 'asc'
}

function parsePostsFieldArgs(storeFieldName: string): PostsFieldArgs | null {
  if (!storeFieldName.startsWith('posts:')) return null

  try {
    const parsed: unknown = JSON.parse(storeFieldName.slice('posts:'.length))
    return isPostsFieldArgs(parsed) ? parsed : null
  } catch {
    return null
  }
}

function isPostsFieldArgs(value: unknown): value is PostsFieldArgs {
  if (!isRecord(value)) return false
  const input = value.input

  return input === undefined || input === null || isRecord(input)
}

function isPostConnection(
  value: Reference | StoreObject,
): value is PostConnection {
  return 'edges' in value
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isConnectionConstrainedByDueAt(args: PostsFieldArgs | null): boolean {
  const filter = args?.input?.filter
  return Boolean(filter?.dueAt || filter?.dueAtPresence)
}

function doesPostMatchConnection(
  args: PostsFieldArgs | null,
  post: PostFilterSnapshot,
): boolean {
  const filter = args?.input?.filter
  if (!filter) return true

  if (!matchesDueAtPresence(filter.dueAtPresence, post.dueAt)) return false
  if (!matchesDueAtRange(filter.dueAt, post.dueAt)) return false
  if (!matchesStatus(filter.status, post.status)) return false
  if (!matchesChannelIds(filter.channelIds, post.channelId)) return false
  if (!matchesTags(filter.tags, post.tagIds)) return false

  return true
}

function matchesDueAtPresence(
  dueAtPresence: DateTimePresence | null | undefined,
  dueAt: string | null,
): boolean {
  if (!dueAtPresence) return true
  if (dueAtPresence === 'absent') return dueAt === null
  return dueAt !== null
}

function matchesDueAtRange(
  dueAtFilter: DueAtFilter | null | undefined,
  dueAt: string | null,
): boolean {
  if (!dueAtFilter) return true
  if (!dueAt) return false

  const dueAtTime = new Date(dueAt).getTime()
  if (Number.isNaN(dueAtTime)) return false

  const startTime = dueAtFilter.start
    ? new Date(dueAtFilter.start).getTime()
    : null
  const endTime = dueAtFilter.end ? new Date(dueAtFilter.end).getTime() : null

  if (startTime !== null && dueAtTime < startTime) return false
  if (endTime !== null && dueAtTime > endTime) return false

  return true
}

function matchesStatus(
  statusFilter: PostStatus[] | PostStatus | null | undefined,
  status: PostStatus | undefined,
): boolean {
  const statusValues = normalizeArray(statusFilter)
  if (statusValues.length === 0) return true
  if (!status) return false
  return statusValues.includes(status)
}

function matchesChannelIds(
  channelIdsFilter: string[] | string | null | undefined,
  channelId: string | null | undefined,
): boolean {
  const channelIds = normalizeArray(channelIdsFilter)
  if (channelIds.length === 0) return true
  if (!channelId) return false
  return channelIds.includes(channelId)
}

function matchesTags(
  tagsFilter: TagComparator | null | undefined,
  tagIds: string[] | undefined,
): boolean {
  if (!tagsFilter) return true
  if (!tagIds) return false

  const matchesSelectedTag = tagsFilter.in.some((tagId) =>
    tagIds.includes(tagId),
  )
  const matchesUntagged = Boolean(tagsFilter.isEmpty && tagIds.length === 0)

  return matchesSelectedTag || matchesUntagged
}

function normalizeArray<T>(value: T[] | T | null | undefined): T[] {
  if (!value) return []
  return Array.isArray(value) ? value : [value]
}

function isString(value: string | undefined): value is string {
  return typeof value === 'string'
}
